import { useTranslation } from 'react-i18next'
import { clsx } from 'clsx'
import { useEffect } from 'react'

import { ChevronIcon, inputClass } from './InquiryField'
import {
  useSidoList,
  useSigunguList,
  type RegcodeEntry,
} from './useRegcode'

interface RegionPickerProps {
  /** Korean sido name, e.g. '서울특별시'. */
  sido?: string
  /** Korean sigungu name, e.g. '강남구'. */
  sigungu?: string
  onChange: (next: { sido?: string; sigungu?: string }) => void
}

export function RegionPicker({ sido, sigungu, onChange }: RegionPickerProps) {
  const { t: mt } = useTranslation('marketing')

  const sidoQuery = useSidoList()
  const sidoList: RegcodeEntry[] = sidoQuery.data ?? []
  const selectedSidoEntry = sidoList.find((s) => s.name === sido)

  const sigunguQuery = useSigunguList(selectedSidoEntry?.code)
  const sigunguList: RegcodeEntry[] = sigunguQuery.data ?? []
  const sigunguError = sigunguQuery.isError

  useEffect(() => {
    if (!sigungu) return
    if (!selectedSidoEntry) return
    if (sigunguQuery.isLoading || sigunguQuery.isError) return
    const stillValid = sigunguList.some(
      (it) => it.name === sigungu,
    )
    if (!stillValid) onChange({ sido, sigungu: undefined })
  }, [
    sigunguList,
    selectedSidoEntry,
    sigungu,
    sigunguQuery.isLoading,
    sigunguQuery.isError,
    onChange,
    sido,
  ])

  return (
    <div className="flex gap-2">
      <div className="relative flex-1">
        <select
          value={sido ?? ''}
          onChange={(e) => {
            const next = e.target.value || undefined
            onChange({ sido: next, sigungu: undefined })
          }}
          className={clsx(inputClass, 'appearance-none pr-9')}
        >
          <option value="" disabled>{mt('consent.province')}
          </option>
          {sidoList.map((s) => (
            <option key={s.code} value={s.name}>
              {s.name}
            </option>
          ))}
        </select>
        <ChevronIcon />
      </div>
      <div className="relative flex-1">
        {sigunguError ? (
          <input
            value={sigungu ?? ''}
            onChange={(e) =>
              onChange({ sido, sigungu: e.target.value || undefined })
            }
            placeholder={mt('consent.district')}
            disabled={!selectedSidoEntry}
            className={clsx(inputClass, 'pr-9')}
          />
        ) : (
          <select
            value={sigungu ?? ''}
            onChange={(e) =>
              onChange({ sido, sigungu: e.target.value || undefined })
            }
            disabled={!selectedSidoEntry || sigunguQuery.isLoading}
            className={clsx(
              inputClass,
              'appearance-none pr-9 disabled:opacity-60',
            )}
          >
            <option value="" disabled>
              {mt('consent.district')}
            </option>
            {sigunguList.map((s) => {
              const label = s.name
              return (
                <option key={s.code} value={label}>
                  {label}
                </option>
              )
            })}
          </select>
        )}
        <ChevronIcon />
      </div>
    </div>
  )
}
